import { AuthClient } from '@supabase/auth-js';
import { env } from '@/env';

/**
 * Auth only — deliberately NOT the umbrella `supabase-js` package.
 *
 * Two reasons. It saves 30–40kB gzipped of postgrest/storage/functions we never call,
 * but the architectural win is larger: there is no `.from().insert()` anywhere in the
 * bundle, so "the browser cannot write to the database" is enforced by the module
 * graph rather than by code review.
 */
export const authClient = new AuthClient({
  url: `${env.supabaseUrl}/auth/v1`,
  headers: { apikey: env.supabaseAnonKey, Authorization: `Bearer ${env.supabaseAnonKey}` },
  storageKey: 'tap-auth',
  autoRefreshToken: true,
  persistSession: true,
  // Handles the ?code= the OAuth callback lands on.
  detectSessionInUrl: true,
  flowType: 'pkce',
});

export async function signInWithGoogle(): Promise<void> {
  /**
   * `hd` pre-filters Google's account chooser to one Workspace domain — it locks the
   * email field's suffix, so nothing else can even be typed.
   *
   * It is a UI hint, NOT a boundary: anyone can rebuild this URL without it, which is
   * why the real checks are server-side and in the database. So it must follow the same
   * setting as those checks, or it silently contradicts them — as it did when the
   * domain was opened for testing and this still refused anything but the company one.
   */
  /*
    `hd` takes ONE domain. Not a list, not a wildcard.
    The setting is now comma-separated to match the server, so sending it verbatim
    would put "thetapacademy.com,gmail.com" into the URL — a value Google cannot
    match, which filters the picker down to nothing and leaves no way to sign in.

    So the hint is only sent when there is exactly one domain to hint AT. With '*' or
    a list, the picker is left open and the server does the deciding — which it does
    either way, since this is a hint and not a boundary.
  */
  const domains = env.allowedEmailDomain
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  const hint = domains.length === 1 && domains[0] !== '*' ? domains[0] : null;

  const { error } = await authClient.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      queryParams: {
        ...(hint ? { hd: hint } : {}),
        prompt: 'select_account',
      },
    },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}

export async function getAccessToken(): Promise<string | null> {
  const { data } = await authClient.getSession();
  return data.session?.access_token ?? null;
}
